(ns app.front.view.admin.ws-export.view
  (:require [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx ::fetch-municipalities
  (fn [_ _]
    {:comm/command {:command    :admin/get-export-municipalities
                    :method     :get
                    :on-success [::fetch-municipalities.success]}}))

(rf/reg-event-db ::fetch-municipalities.success
  (fn [db [_ _ municipalities]]
    (-> db
        (assoc ::municipalities municipalities))))

(rf/reg-event-fx ::fetch-exports
  (fn [_ _]
    {:comm/command {:command    :admin/list-workspace-exports
                    :method     :get
                    :on-success [::fetch-exports.success]}}))

(rf/reg-event-db ::fetch-exports.success
  (fn [db [_ _ {:keys [workspaces packages]}]]
    (-> db
        (assoc ::workspaces workspaces
               ::packages packages))))

(rf/reg-event-fx ::export-workspace
  (fn [_ [_ workspace-id]]
    {:comm/command {:command    :admin/export-workspace
                    :data       (util/mmap workspace-id)
                    :on-success [::export-workspace.success workspace-id]}}))

(rf/reg-event-fx ::export-workspaces
  (fn [_ [_ {:keys [country municipality]} year month]]
    {:comm/command {:command    :admin/export-workspaces
                    :data       (util/mmap country municipality year month)
                    :on-success [::export-workspaces.success]}}))

(rf/reg-event-db ::export-workspaces.success
  (fn [db [_ status resp]]
    (if (= status 200)
      (-> db
          (update ::packages
                  conj
                  {:db/id          (:export-id resp)
                   :entity/created (js/Date.)
                   :domain/state   :export-request.state/processing}))
      db)))

(rf/reg-event-db ::export-workspace.success
  (fn [db [_  workspace-id status resp]]
    (if (= 200 status)
      (-> db
          (update-in [::workspaces workspace-id] merge {:domain/state   :workspace-export-request.state/waiting
                                                        :entity/created (js/Date.)
                                                        :db/id          (:export-id resp)}))
      db)))

(rf/reg-sub ::workspaces
  (fn [db _]
    (sort-by first (::workspaces db))))

(rf/reg-sub ::packages
  (fn [db _]
    (sort-by :entity/created (::packages db))))

(defn export-button [on-click]
  [cc/button (util/mmap {:class-name "mt-2"}
                        on-click)
   "Export"])

(defn export-single []
  (r/with-let [*workspace-id (r/atom "")]
    [:div
     [ui/text-input {:label-text  "Workspace ID"
                     :placeholder "Like CA-123123-B-2022-42"
                     :value       @*workspace-id
                     :on-change   #(reset! *workspace-id (futil/tval %))}]
     [export-button #(>evt [::export-workspace @*workspace-id])]]))

(rf/reg-sub ::municipalities-raw :-> ::municipalities)

(rf/reg-sub ::municipalities
  :<- [::municipalities-raw]
  (fn [municipalities _]
    (sort-by :municipality-name municipalities)))

(defn export-municipality []
  (r/with-let [*municipality (r/atom nil)
               *year         (r/atom (.getFullYear (js/Date.)))
               *month        (r/atom nil)]
    [:div
     [ui-select/select {:id           "select-muni"
                        :label-text   "Select municipality"
                        :required     true
                        :allow-empty? false
                        :val->str     :municipality-name
                        :value        @*municipality
                        :on-change    (fn [municipality]
                                        (reset! *municipality municipality))
                        :values       (<sub [::municipalities])}]
     [:div.mt-2
      [ui/number-input (util/mmap {:id         "year"
                                   :required   true
                                   :label-text "Created year"
                                   :step       1
                                   :on-change  #(reset! *year (futil/parse-int (futil/tval %)))
                                   :value      @*year})]]
     [:div.mt-2
      [ui/number-input (util/mmap {:id         "month"
                                   :required   false
                                   :label-text "Created month (1-12). If left empty, export the whole year."
                                   :step       1
                                   :on-change  #(reset! *month (futil/parse-int (futil/tval %)))
                                   :value      @*month
                                   :min        1
                                   :max        12})]]
     [export-button #(>evt [::export-workspaces @*municipality @*year @*month])]]))

(defn export-request []
  (r/with-let [*tab (r/atom :single)]
    [:div.mt-8
     [:h2.mb-2 "Start another export"]
     [ui-cs/content-switcher {:items     [:single :municipality]
                              :val->str  {:single       "Single workspace"
                                          :municipality "All workspaces for a given municipality"}
                              :current   @*tab
                              :on-change #(reset! *tab %)
                              :class     "mb-4"}]
     (case @*tab
       :single
       [export-single]

       :municipality
       [export-municipality]

       nil)]))

(defn title-and-content [title content]
  [:div.mb-8
   [:h2.mb-2 title]
   content])

(defn params->str [{:keys [country municipality year month] :as params}]
  (if params
    (if month
      (str (name country) "-" municipality " / " year " / " month)
      (str (name country) "-" municipality " / " year))
    "-"))

(defn view []
  (let [tr               (<sub [:tr])
        format-date-time (<sub [::date-util/date-time-fn])]
    [:div.cloudpermit-page.admin-page
     [:h1 "Export workspaces"]
     [:span
      "You can export workspaces here, either one-by-one or as a single package for a municipality."]
     [ui/help
      (str "Note that the data contained does not have any access control rules applied, which means it will "
           "contain e.g. data that is normally hidden from the applicant.")]
     [title-and-content
      "Exported workspaces"
      [ui-table/modern-responsive-table
       (util/mmap {:items             (<sub [::workspaces])
                   :empty-placeholder "No single-workspace exports. Some workspaces that have previously been exported may be included in a package."
                   :cols              [[:workspace/id first]
                                       [:entity/creator (comp user-model/full-name :entity/creator second)]
                                       [:entity/language #(some-> % second :entity/language name)]
                                       [:entity/created (comp format-date-time :entity/created second)]
                                       [:domain/state (comp tr vector :domain/state second)]]
                   :id-func           (comp :db/id second)
                   :link              {:make-href   (fn [[_ {export-id :db/id :as export}]]
                                                      (when (du/in-state? :workspace-export-request.state/ready export)
                                                        (str "/api/command/admin/download-workspace-export-file?workspace-export-id=" export-id)))
                                       :description "Download file"}}
                  tr)]]
     [title-and-content
      "Export packages"
      [ui-table/modern-responsive-table
       (util/mmap {:items             (<sub [::packages])
                   :empty-placeholder "No export packages"
                   :cols              [[:entity/creator (comp user-model/full-name :entity/creator)]
                                       [:entity/created (comp format-date-time :entity/created)]
                                       [:export-request/parameters (comp params->str :export-request/parameters)]
                                       [:domain/state (comp tr vector :domain/state)]]
                   :id-func           :db/id
                   :link              {:make-href   (fn [{export-id :db/id :as export}]
                                                      (when (du/in-state? :export-request.state/ready export)
                                                        (str "/api/command/admin/download-workspace-export-file?export-id=" export-id)))
                                       :description "Download file"}}
                  tr)]]
     [export-request]]))

(def route
  {:name        :view/admin.ws-export
   :view        view
   :controllers [{:start (fn []
                           (>evt [::fetch-municipalities])
                           (>evt [::fetch-exports]))}]})
